import reportOptimoveLogin from "@casino/client/optimove/reportLogin";
import useRedirectUrl from "@casino/legacy/client/components/Auth/hooks/useRedirectUrl";
import setDataToGib from "@casino/legacy/client/components/Auth/Shared/utils/setDataToGib";
import toGenericErrors from "@casino/legacy/client/components/Auth/Shared/utils/toGenericErrors";
import useRefCode from "@casino/legacy/client/hooks/cookies/marketing/useRefCode";
import usePhoneNumberHint from "@casino/legacy/client/hooks/usePhoneNumberHint";
import useLoginError from "@casino/legacy/client/hooks/user/useLoginError";
import useSite from "@casino/legacy/client/hooks/useSite";
import useAppContext from "@casino/legacy/shared/context/AppContext/useAppContext";
import authType from "@casino/legacy/shared/utils/auth/authType";
import serialize from "@casino/legacy/shared/utils/serialize";
import SessionStorage from "@casino/legacy/shared/utils/storage/SessionStorage";
import FRIENDREF_COOKIE from "@workspace/casino_config/cookies/FRIENDREF_COOKIE";
import REF_AFF from "@workspace/casino_config/cookies/REF_AFF";
import type { TelegramMutation } from "@workspace/casino_core/mutations/TelegramMutation";
import type { ErrorsFragment } from "@workspace/casino_core/mutations/UpdateUserSettingsMutation";
import type { GraphqlResult } from "@workspace/casino_core/useQuery/GraphqlResult";
import { getCookie, setCookie } from "@workspace/cookies/browser";
import { useRouter } from "next/navigation";
import type DataAuthType from "../@types/DataAuthType";
import useTelegram from "./useTelegram";

type Args = {
  dataAuth: DataAuthType;
  code?: string;
  otp?: string;
  setOtpError?: () => void;
};

const useTelegramAuth = (onSuccess?: () => void) => {
  const { site } = useSite();
  const appContext = useAppContext();
  const refCode = useRefCode();
  const refAff = getCookie(REF_AFF);
  const friendRef = getCookie(FRIENDREF_COOKIE);
  const cid = getCookie("cid");
  const redirect = useRedirectUrl();
  const router = useRouter();
  const { setError } = useLoginError();
  const { userManagementAuthWithTelegram } = useTelegram();
  const { setPhoneNumberHint } = usePhoneNumberHint();

  const handleTelegramAuth = async ({ dataAuth, otp, setOtpError }: Args) => {
    const processResult = (
      result: GraphqlResult<TelegramMutation>,
      telegramData: string,
    ) => {
      const encodedRedirectUrl = encodeURIComponent(redirect as string);
      const {
        token = "",
        errors,
        sessionId,
        userManagement,
      } = result?.data?.userManagementAuthWithTelegram || {};
      const criticalErrors = getCriticalErrors(errors);
      const hasOtpError = getOtpError(criticalErrors);

      if (!errors) {
        console.log(`Setting token from Telegram`);
        setCookie("token", token);

        if (userManagement?.profile?._id && sessionId) {
          setDataToGib(userManagement?.profile?._id, sessionId);
        }
        const optimoveUserId = userManagement?.profile?._id;
        if (optimoveUserId) {
          reportOptimoveLogin(optimoveUserId);
        }

        if (onSuccess) {
          onSuccess();
          return router.refresh();
        }
        const redirectPath =
          redirect.includes("login") || redirect.includes("register");
        const defaultRedirect =
          redirectPath || !redirect.length ? "/" : redirect;
        router.push(defaultRedirect, undefined);
        return router.refresh();
      }
      if (criticalErrors && hasOtpError) {
        setOtpError?.();
        router.push(
          `/two-factor-authentication?redirect=${encodedRedirectUrl}`,
          undefined,
        );
        return { loginError: true };
      }
      if (criticalErrors && !hasOtpError) {
        setError(criticalErrors);
        setPhoneNumberHint(toGenericErrors(errors));
        router.push(`/login?redirect=${encodedRedirectUrl}`, undefined);
        return { loginError: true };
      }

      const missingFields = errors
        .filter((el) => el?.message === "IS_REQUIRED")
        .map((el) => el?.params?.[0]?.value);
      SessionStorage.setItem(
        "social_auth_missing_fields",
        missingFields.join(","),
      );
      authType.set("telegram");
      SessionStorage.setItem("social_auth_token", telegramData);
      router.push(`/complete-registration?redirect=${encodedRedirectUrl}`);

      return null;
    };
    const getCriticalErrors = (errors?: Array<ErrorsFragment | undefined>) =>
      errors
        ?.filter((el) => el?.message !== "IS_REQUIRED")
        .map((el) => el?.message)
        .join(",") ?? "";
    const getOtpError = (criticalErrors: string) => {
      if (criticalErrors.includes("OTP") || criticalErrors === "INVALID") {
        return true;
      }
      return false;
    };

    const domain = global.location?.hostname;
    const language = appContext.language;
    const telegramData = serialize(dataAuth);
    if (!telegramData) {
      return;
    }

    const result = await userManagementAuthWithTelegram({
      telegramData,
      domain,
      language,
      refCode,
      refAff,
      cid,
      site,
      otp,
      friendRef,
    });

    processResult(result, telegramData);
  };

  return { handleTelegramAuth };
};

export default useTelegramAuth;
